import { useMutation, useQuery } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Building2, GraduationCap, Loader, MailCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { BrandLogo } from '~/components/BrandLogo'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_APP_CONFIG } from '~/gql/app-config/app-config'
import { GQL_REQUEST_TRIAL } from '~/gql/trial/trial'

/**
 * Parcours d'inscription qualifié en libre-service (#155).
 *
 * Étapes : choix du profil → (apprenant/formateur = impasse assumée, on n'ouvre
 * pas de compte sans organisme invitant) ou formulaire qualifié → écran « vérifiez
 * votre boîte mail ». L'API répond toujours pareil pour ne pas devenir un oracle
 * d'existence : le succès mène à l'écran de vérification quoi qu'il arrive, seul
 * un domaine email grand public est refusé explicitement (adresse pro exigée).
 */
type Step = 'choice' | 'learner' | 'form' | 'sent'

function Signup() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('choice')
  const [sentTo, setSentTo] = useState('')

  const signupSchema = z.object({
    firstName: z.string().min(1, { message: t('input.required.message') }),
    lastName: z.string().min(1, { message: t('input.required.message') }),
    email: z.string().email({ message: t('input.email.message') }),
    orgName: z.string().min(1, { message: t('input.required.message') }),
    phone: z.string().optional(),
    jobTitle: z.string().optional(),
  })

  type SignupFormValues = z.infer<typeof signupSchema>

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { firstName: '', lastName: '', email: '', orgName: '', phone: '', jobTitle: '' },
  })

  const [requestTrial, { loading }] = useMutation(GQL_REQUEST_TRIAL)

  /**
   * La route vit sous le basename d'org (`/o/<slug>/signup`), donc elle reste
   * atteignable en direct sur une org cliente même une fois le lien retiré de
   * l'écran de connexion (#216). On la referme ici plutôt que de compter sur une
   * URL non devinable : ouvrir un compte plateforme n'est pas une action qui a un
   * sens depuis l'espace d'un client.
   *
   * On interroge la config plutôt que `useBranding()` pour distinguer « pas
   * encore chargé » de « masqué » — le contexte, lui, part masqué par défaut et
   * redirigerait avant même d'avoir la réponse. Une query en échec redirige aussi :
   * mieux vaut renvoyer au login que d'ouvrir le formulaire sans savoir sur
   * quelle org on est.
   */
  const { data: config, loading: configLoading } = useQuery(GQL_APP_CONFIG)
  if (configLoading) return null
  if (!config?.appConfig.signupCtaVisible) return <Navigate to="/login" replace />

  const onSubmit = async (values: SignupFormValues) => {
    try {
      await requestTrial({
        variables: {
          input: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            orgName: values.orgName,
            phone: values.phone?.trim() || undefined,
            jobTitle: values.jobTitle?.trim() || undefined,
          },
        },
      })
      setSentTo(values.email)
      setStep('sent')
    } catch (err) {
      // Seul refus explicite : une adresse grand public (gmail…) — on exige une
      // adresse professionnelle. Toute autre erreur reste générique.
      if (String(err).includes('PUBLIC_EMAIL_DOMAIN')) {
        form.setError('email', { message: t('signup.error.publicEmail') })
      } else {
        toast.error(t('signup.error.generic'))
      }
    }
  }

  return (
    <div className="flex w-screen min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-xl w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <CardTitle className="font-heading text-2xl tracking-tight">
            {step === 'sent' ? t('signup.sent.title') : t('signup.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {step === 'choice' && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">{t('signup.choice.subtitle')}</p>
              <button
                type="button"
                onClick={() => setStep('form')}
                className="flex items-start gap-4 rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-accent/40"
              >
                <Building2 className="mt-1 size-6 shrink-0 text-primary" />
                <span className="flex flex-col gap-1">
                  <span className="font-medium">{t('signup.choice.org.title')}</span>
                  <span className="text-sm text-muted-foreground">{t('signup.choice.org.description')}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setStep('learner')}
                className="flex items-start gap-4 rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-accent/40"
              >
                <GraduationCap className="mt-1 size-6 shrink-0 text-primary" />
                <span className="flex flex-col gap-1">
                  <span className="font-medium">{t('signup.choice.learner.title')}</span>
                  <span className="text-sm text-muted-foreground">{t('signup.choice.learner.description')}</span>
                </span>
              </button>
              <div className="mt-2 text-center text-sm">
                <a onClick={() => navigate('/login')} className="underline underline-offset-4 cursor-pointer">
                  {t('signup.login')}
                </a>
              </div>
            </div>
          )}

          {step === 'learner' && (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <GraduationCap className="size-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('signup.learner.description')}</p>
              </div>
              <Button variant="outline" onClick={() => setStep('choice')} className="w-full">
                <ArrowLeft className="mr-2 size-4" />
                {t('signup.back')}
              </Button>
            </div>
          )}

          {step === 'form' && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
                <p className="text-sm text-muted-foreground">{t('signup.form.subtitle')}</p>

                <div className="grid gap-6 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="grid gap-2">
                            <Label htmlFor="firstName">{t('common.firstname')}</Label>
                            <Input id="firstName" type="text" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="grid gap-2">
                            <Label htmlFor="lastName">{t('common.lastname')}</Label>
                            <Input id="lastName" type="text" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="grid gap-2">
                          <Label htmlFor="email">{t('signup.form.email')}</Label>
                          <Input id="email" type="email" placeholder="prenom@organisme.fr" {...field} />
                          <p className="text-xs text-muted-foreground">{t('signup.form.email.hint')}</p>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="orgName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="grid gap-2">
                          <Label htmlFor="orgName">{t('signup.form.orgName')}</Label>
                          <Input id="orgName" type="text" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-6 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="jobTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="grid gap-2">
                            <Label htmlFor="jobTitle">{t('signup.form.jobTitle')}</Label>
                            <Input id="jobTitle" type="text" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="grid gap-2">
                            <Label htmlFor="phone">{t('signup.form.phone')}</Label>
                            <Input id="phone" type="tel" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep('choice')} className="shrink-0">
                    <ArrowLeft className="size-4" />
                  </Button>
                  <Button type="submit" disabled={loading} className="w-full">
                    {loading && <Loader className="mr-2 size-4 animate-spin" />}
                    {t('signup.form.submit')}
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {step === 'sent' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <MailCheck className="size-12 text-primary" />
              <p className="text-sm text-muted-foreground">
                {t('signup.sent.description', { email: sentTo })}
              </p>
              <a onClick={() => navigate('/login')} className="underline underline-offset-4 cursor-pointer text-sm">
                {t('signup.login')}
              </a>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default Signup
